import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { fetchOrder, OrderResponse, updateOrder, type UpdateOrderPayload } from '../../../api/orders';

import ServiceAssignmentsTable from './ServiceAssignmentsTable';
import { useServiceAssignments } from '../hooks/useServiceAssignments';
import Notification from '../../../components/midone/components/Base/Notification';
import type { NotificationElement } from '../../../components/midone/components/Base/Notification';
import { uploadOrderFiles, fetchOrderFiles, deleteOrderFile, type OrderFileRecord } from '../../../api/files';



interface OrderTechnicianCompletedProps {
  orderId: string | null;
  showHeader?: boolean;
  onBackToOrders?: () => void;
  onReturnToIntake?: (orderId: string | null) => void;
  onCompleted?: (orderId: string) => void;
  onOrderUpdate?: (order: OrderResponse) => void;
}

type ToastState = {
  id: number;
  title: string;
  message: string;
};

const TOAST_DURATION = 2500;

const formatDateTime = (value: string | null) => {
  if (!value) {
    return '—';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return '—';
  }

  return date.toLocaleString();
};

const OrderTechnicianCompleted: FC<OrderTechnicianCompletedProps> = ({
  orderId,
  showHeader = true,
  onCompleted,
  onOrderUpdate,
}) => {
  const [order, setOrder] = useState<OrderResponse | null>(null);
  const [orderError, setOrderError] = useState<string | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  const [jobCompleted, setJobCompleted] = useState(false);
  const [completedAt, setCompletedAt] = useState<string | null>(null);
  const [technicianNotes, setTechnicianNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [nextDueByItem, setNextDueByItem] = useState<Record<string, string>>({});
  const [selectedFiles, setSelectedFiles] = useState<File[] | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const MAX_MB = Number(process.env.REACT_APP_MAX_UPLOAD_MB ?? 25);
  const [existingFiles, setExistingFiles] = useState<OrderFileRecord[]>([]);
  const [filesLoading, setFilesLoading] = useState(false);
  const [filesError, setFilesError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const [toast, setToast] = useState<ToastState | null>(null);
  const toastRef = useRef<NotificationElement | null>(null);

  const {
    assignments: serviceAssignments,
    setAssignmentCompleted,
    setAssignmentNotes,
    dirty: serviceAssignmentsDirty,
    payload: serviceAssignmentPayload,
    serviceItems,
  } = useServiceAssignments(order);

  useEffect(() => {
    if (!orderId) {
      setOrder(null);
      setJobCompleted(false);
      setCompletedAt(null);
      setOrderError('Order ID not specified.');
      setLoadingOrder(false);
      return;
    }

    let isMounted = true;
    setLoadingOrder(true);
    setOrderError(null);

    fetchOrder(orderId)
      .then((data) => {
        if (!isMounted) {
          return;
        }
        setOrder(data);
        const nextDue: Record<string, string> = {};
        (data.items ?? []).forEach((it) => {
          if (typeof it.nextDueAfterDays === 'number') {
            nextDue[it.id] = String(it.nextDueAfterDays);
          }
        });
        setNextDueByItem(nextDue);
        const timestamp = data.technicianCompletedTheJobAt ?? null;
        setCompletedAt(timestamp);
        setJobCompleted(Boolean(timestamp));
        setTechnicianNotes(data.technicianNotes ?? '');
        setSaveError(null);
      })
      .catch((error) => {
        if (!isMounted) {
          return;
        }
        const message = error instanceof Error ? error.message : 'Failed to load order details.';
        setOrder(null);
        setJobCompleted(false);
        setCompletedAt(null);
        setTechnicianNotes('');
        setOrderError(message);
      })
      .finally(() => {
        if (isMounted) {
          setLoadingOrder(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [orderId]);

  useEffect(() => {
    if (!toast) {
      return;
    }

    toastRef.current?.showToast?.();

    const handle = window.setTimeout(() => {
      toastRef.current?.hideToast?.();
      setToast(null);
    }, TOAST_DURATION);

    return () => {
      window.clearTimeout(handle);
      toastRef.current?.hideToast?.();
    };
  }, [toast]);

  const initialCompletedAt = useMemo(() => order?.technicianCompletedTheJobAt ?? null, [order]);
  const initialTechnicianNotes = useMemo(() => order?.technicianNotes ?? '', [order]);
  const normalizedNotes = technicianNotes.trim();
  const statusDirty = (initialCompletedAt ?? null) !== (completedAt ?? null)
    || initialTechnicianNotes.trim() !== normalizedNotes;
  const overallDirty = statusDirty || serviceAssignmentsDirty;

  const completionBanner = useMemo(() => {
    if (serviceItems.length === 0) {
      return {
        tone: 'success',
        text: 'No services linked. Saving will mark the job as Completed.',
      } as const;
    }
    const allCompleted = serviceAssignmentPayload.every((a) => Boolean(a.completed));
    if (allCompleted) {
      return {
        tone: 'success',
        text: 'All services are completed. Saving will mark the job as Completed.',
      } as const;
    }
    return {
      tone: 'info',
      text: 'Some services are still pending. Saving will keep the status In progress.',
    } as const;
  }, [serviceItems.length, serviceAssignmentPayload]);

  // Global completion toggle removed; completion is derived from service assignments

  const handleSave = async () => {
    if (!orderId) {
      return;
    }

    setSaving(true);
    setSaveError(null);

    const payload: UpdateOrderPayload = {
      technicianNotes: normalizedNotes.length ? normalizedNotes : null,
    };

    if (serviceItems.length) {
      // Merge nextDueAfterDays into service assignments
      payload.serviceAssignments = serviceAssignmentPayload.map((a) => {
        const raw = nextDueByItem[a.orderItemId] ?? '';
        const nextDueAfterDays = raw.trim() === '' ? undefined : Number(raw);
        return { ...a, nextDueAfterDays } as any;
      });
      const allCompleted = serviceAssignmentPayload.every((a) => Boolean(a.completed));
      payload.technicianCompletedTheJobAt = allCompleted ? (completedAt ?? new Date().toISOString()) : null;
    } else {
      // No service items: saving marks job as completed
      payload.technicianCompletedTheJobAt = completedAt ?? new Date().toISOString();
    }

    try {
      const updated = await updateOrder(orderId, payload);
      setOrder(updated);
      const timestamp = updated.technicianCompletedTheJobAt ?? null;
      setCompletedAt(timestamp);
      setJobCompleted(Boolean(timestamp));
      setTechnicianNotes(updated.technicianNotes ?? '');
      onOrderUpdate?.(updated);
      setToast({
        id: Date.now(),
        title: 'Saved',
        message: 'Technician completion status has been updated.',
      });
      if (updated.technicianCompletedTheJobAt && onCompleted) {
        onCompleted(updated.id);
      }
    } catch (error) {
      const message =
        error instanceof Error
          ? error.message || 'Failed to update technician completion status.'
          : 'Failed to update technician completion status.';
      setSaveError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!orderId || !selectedFiles || selectedFiles.length === 0) return;
    setUploading(true);
    setUploadMsg(null);
    try {
      await uploadOrderFiles({ orderId, type: 'technician', files: selectedFiles });
      setUploadMsg('Files uploaded');
      setSelectedFiles([]);
      const latest = await fetchOrderFiles(orderId, 'technician');
      setExistingFiles(latest);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Upload failed';
      setUploadMsg(msg);
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (!orderId) {
      setExistingFiles([]);
      return;
    }
    setFilesLoading(true);
    setFilesError(null);
    fetchOrderFiles(orderId, 'technician')
      .then(setExistingFiles)
      .catch((e) => setFilesError(e instanceof Error ? e.message : 'Failed to load files'))
      .finally(() => setFilesLoading(false));
  }, [orderId, uploading]);

  const badgeClass = jobCompleted
    ? 'bg-emerald-100 text-emerald-700 border border-emerald-200'
    : 'bg-slate-200 text-slate-600 border border-slate-300';

  return (
    <div className="space-y-6">
      <Notification
        className="flex w-80 max-w-full flex-col items-center gap-1 rounded-xl border border-primary/40 bg-primary/5 px-4 py-3 text-center text-primary shadow-lg"
        options={{
          duration: TOAST_DURATION,
          gravity: 'top',
          position: 'right',
          stopOnFocus: true,
          close: false,
          offset: {
            x: 24,
            y: 24,
          },
        }}
        getRef={(el: NotificationElement) => {
          toastRef.current = el;
        }}
      >
        {toast ? (
          <div className="space-y-1">
            <p className="text-sm font-semibold uppercase tracking-wide text-primary">{toast.title}</p>
            <p className="text-xs text-primary/80">{toast.message}</p>
          </div>
        ) : null}
      </Notification>

      {showHeader ? (
        <div className="space-y-2">
          <h2 className="text-2xl font-semibold text-slate-800">Technician completion</h2>
          <p className="text-sm text-slate-500">
            Confirm when the assigned technicians have completed their work on this job card.
          </p>
        </div>
      ) : null}

      {orderError ? (
        <div className="rounded-xl border border-danger/40 bg-danger/10 px-4 py-3 text-sm text-danger">{orderError}</div>
      ) : null}

      <div className="rounded-2xl  bg-white p-6 shadow-sm">
        {loadingOrder ? (
          <p className="text-sm text-slate-500">Loading order details…</p>
        ) : !order ? (
          <p className="text-sm text-slate-500">Select an order to review technician completion.</p>
        ) : (
          <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-wide text-slate-400">Order number</p>
                <p className="text-lg font-semibold text-slate-800">{order.orderNumber}</p>
              </div>
              <span className={`rounded-full px-3 py-1 text-xs font-semibold ${badgeClass}`}>
                {jobCompleted ? 'Completed' : 'Pending'}
                {overallDirty ? ' • Unsaved' : ''}
              </span>
            </div>

            {/* Global completion checkbox removed; completion inferred from service assignments */}

            <div
              className={`rounded-xl px-4 py-3 text-xs ${
                completionBanner.tone === 'success'
                  ? 'border border-emerald-300/60 bg-emerald-50 text-emerald-700'
                  : 'border border-sky-300/60 bg-sky-50 text-sky-700'
              }`}
            >
              {completionBanner.text}
            </div>

            <ServiceAssignmentsTable
              order={order}
              assignments={serviceAssignments}
              disabled={saving}
              readOnly
              onCompletedChange={setAssignmentCompleted}
              onNotesChange={setAssignmentNotes}
              allowStatusEditing
              allowNotesEditing
              allowNextDueEditing
              nextDueByItem={nextDueByItem}
              onNextDueChange={(itemId, value) => setNextDueByItem((prev) => ({ ...prev, [itemId]: value }))}
            />

            <div>
              <textarea
                value={technicianNotes}
                onChange={(event) => setTechnicianNotes(event.target.value)}
                placeholder="Add any remarks from the technician handover."
                rows={4}
                className="mt-2 w-full rounded-xl border border-slate-300 px-4 py-3 text-sm text-slate-700 shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
                disabled={saving}
              />
              <p className="mt-1 text-xs text-slate-400">Visible to downstream steps for context on the work performed.</p>
            </div>

            <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-500">
              <p>Completion recorded: {formatDateTime(completedAt)}</p>
              {overallDirty ? (
                <p className="mt-1 text-amber-600">You have unsaved changes. Save to update the completion log.</p>
              ) : null}
            </div>

            <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-3 text-xs">
              <p className="text-slate-600 mb-2">Upload technician work photos</p>
              <div className="flex items-center gap-3">
                <input
                  type="file"
                  accept="image/*,video/*"
                  multiple
                  onChange={(e) => {
                    const list = e.target.files ? Array.from(e.target.files) : [];
                    const maxBytes = MAX_MB * 1024 * 1024;
                    const kept = list.filter((f) => f.size <= maxBytes);
                    const dropped = list.length - kept.length;
                    setSelectedFiles(kept.length ? kept : null);
                    if (dropped > 0) {
                      setUploadMsg(`Some files were too large and skipped (max ${MAX_MB}MB each).`);
                    } else {
                      setUploadMsg(null);
                    }
                  }}
                  disabled={!orderId || uploading}
                />
                <span className="text-[11px] text-slate-500">Images/Videos up to {MAX_MB}MB each</span>
                <button
                  type="button"
                  className="inline-flex items-center justify-center rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-primary hover:text-primary disabled:opacity-60"
                  onClick={handleUpload}
                  disabled={!orderId || !selectedFiles || selectedFiles.length === 0 || uploading}
                >
                  {uploading ? 'Uploading…' : 'Upload selected'}
                </button>
                {uploadMsg ? <span className="text-xs text-slate-500">{uploadMsg}</span> : null}
              </div>
              {selectedFiles && selectedFiles.length > 0 ? (
                <div className="mt-3 grid grid-cols-4 md:grid-cols-6 gap-2">
                  {selectedFiles.map((f, idx) => {
                    const isVideo = (f.type || '').startsWith('video/');
                    const src = URL.createObjectURL(f);
                    return (
                      <div key={`${f.name}-${idx}`} className="rounded-lg border border-slate-200 p-1 bg-white">
                        {isVideo ? (
                          <video src={src} className="aspect-square w-full object-cover rounded" muted playsInline />
                        ) : (
                          <img src={src} alt={f.name} className="aspect-square w-full object-cover rounded" />
                        )}
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <div className="truncate text-xs text-slate-500" title={f.name}>{f.name}</div>
                          <button
                            type="button"
                            className="text-[11px] text-danger hover:underline"
                            onClick={() => setSelectedFiles((prev) => (prev ? prev.filter((_, i) => i !== idx) : prev))}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              <div className="mt-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Uploaded</p>
                {filesLoading ? (
                  <div className="text-xs text-slate-500 mt-1">Loading…</div>
                ) : filesError ? (
                  <div className="text-xs text-danger mt-1">{filesError}</div>
                ) : existingFiles.length === 0 ? (
                  <div className="text-xs text-slate-400 mt-1">No files yet.</div>
                ) : (
                  <div className="mt-2 grid grid-cols-4 md:grid-cols-6 gap-2">
                  {existingFiles.map((f) => {
                    const isVideo = (f.contentType || '').startsWith('video/');
                    return (
                      <div key={f.id} className="rounded-lg border border-slate-200 p-1 bg-white">
                        <a href={f.url} target="_blank" rel="noreferrer">
                          {isVideo ? (
                            <video src={f.url} className="aspect-square w-full object-cover rounded" muted playsInline />
                          ) : (
                            <img src={f.url} alt={f.originalName ?? ''} className="aspect-square w-full object-cover rounded" />
                          )}
                        </a>
                        <div className="mt-1 flex items-center justify-between gap-2">
                          <div className="truncate text-xs text-slate-500" title={f.originalName ?? ''}>{f.originalName ?? ''}</div>
                          <button
                            className="text-[11px] text-danger hover:underline disabled:opacity-50"
                            onClick={async () => {
                              if (!orderId) return;
                              if (!window.confirm('Delete this file?')) return;
                              setDeletingId(f.id);
                              try {
                                await deleteOrderFile(orderId, f.id, f.s3Key);
                                setExistingFiles((prev) => prev.filter((x) => x.id !== f.id));
                                setUploadMsg('Deleted');
                              } catch (e) {
                                setUploadMsg(e instanceof Error ? e.message : 'Delete failed');
                              } finally {
                                setDeletingId(null);
                              }
                            }}
                            disabled={deletingId === f.id}
                          >
                            {deletingId === f.id ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  </div>
                )}
              </div>
            </div>

            {saveError ? (
              <div className="rounded-lg border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger">{saveError}</div>
            ) : null}

            <div className="flex items-center justify-end gap-3 mt-2">
              <button
                type="button"
                className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white shadow transition hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-60"
                onClick={handleSave}
                disabled={saving || !(overallDirty || serviceItems.length === 0)}
              >
                {saving ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderTechnicianCompleted;
